import type { ProviderName } from '../interfaces/IAuth.js';

/*
 * Error classes shared code throws. They live here — not in either app's
 * `apis/auth.ts` — because a thrown class must be ONE identity: an `instanceof`
 * check in app code only matches when the app and the shared factory reference
 * the same class object. Both apps re-export these from their `apis/auth.ts`.
 */

export class UnauthorizedError extends Error {
  constructor() { super('unauthorized'); this.name = 'UnauthorizedError'; }
}

export class ProviderErrorResponse extends Error {
  constructor(
    public readonly code:
      | 'PROVIDER_NOT_CONNECTED'
      | 'PROVIDER_DISCONNECTED'
      // Genba has no refresh token, so an expired session surfaces as a 401.
      | 'PROVIDER_REAUTH_REQUIRED',
    public readonly provider: ProviderName,
  ) {
    super(`${code}: ${provider}`);
    this.name = 'ProviderErrorResponse';
  }
}

/**
 * A request the server answered with a failure no typed error above names.
 * Carries the HTTP `status` and the envelope's `code`, so a caller can tell a
 * refusal (a 4xx: the same request gets the same answer) from a server that
 * could not answer right now — and can word its message by the code (and the
 * envelope's `details`, e.g. an import's parse `reason`). The message is the
 * plain "<what> failed: <status> <server message>" line.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
    public readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The envelope `code` a failed request carried, if it was an `ApiError`. */
export function apiErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}

/**
 * Whether sending the same request again could succeed: the server never
 * answered (`fetch` rejects with a `TypeError` when the network fails), or it
 * answered "not right now" — a 5xx, a timeout (408) or a rate limit (429).
 * Every other answer is the server's decision about THIS request (a 4xx, or
 * one of the typed refusals above), and repeating it only repeats the answer.
 */
export function isTransientFailure(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 408 || err.status === 429;
  return err instanceof TypeError;
}

/**
 * Whether a 401 means the caller's D-ORCA session is over.
 *
 * An upstream provider (Trusstor, Genba) rides the same status on a DIFFERENT
 * credential: the D-ORCA session is still valid and the user needs a reconnect
 * prompt, not a logout.
 *
 * The web states the same rule inline in `applyUnauthorizedPolicy`
 * (`frontend/src/apis/auth.ts`) and does not call this yet — the two must be
 * changed together until it does.
 */
export function endsSession(errorCode: string | undefined): boolean {
  return errorCode !== 'PROVIDER_REAUTH_REQUIRED' && errorCode !== 'UPSTREAM_UNAUTHORIZED' && errorCode !== 'INVALID_CREDENTIALS';
}

// The shared failure-mapper — also used by the other factories in this folder.
export async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  const code: string | undefined = body?.error?.code;

  // Genba has no refresh: its expired session rides a 401 with its own code,
  // a provider error the caller can send to the reconnect page. Any other 401
  // reaches us only after authedFetch's refresh-and-retry already failed and
  // dispatched `dorca:auth-expired`; we just surface the error type so React
  // Query lands in an error state until the redirect fires.
  if (res.status === 401) {
    const provider = body?.error?.details?.provider;
    if (code === 'PROVIDER_REAUTH_REQUIRED' && provider) throw new ProviderErrorResponse(code, provider);
    throw new UnauthorizedError();
  }
  if (res.status === 409 && (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')) {
    const provider = body?.error?.details?.provider;
    if (provider) throw new ProviderErrorResponse(code, provider);
  }
  throw new ApiError(res.status, code, `request failed: ${res.status} ${body?.error?.message ?? ''}`);
}
